export type ThemePreference = "light" | "dark" | "system";
export type ThemeMode = "light" | "dark";

const THEME_STORAGE_KEY = "tlx-theme";
const THEME_ATTRIBUTE = "data-theme";
export const THEME_CHANGE_EVENT = "tlx-theme-change";
const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

const isThemePreference = (value: string | null): value is ThemePreference => {
  return value === "light" || value === "dark" || value === "system";
};

export const readPreferenceFromStorage = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "light";
  } catch {
    return "light";
  }
};

const writePreferenceToStorage = (preference: ThemePreference): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Ignore storage failures; theme still applies for current session.
  }
};

const resolveTheme = (preference: ThemePreference): ThemeMode => {
  if (preference !== "system") {
    return preference;
  }
  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? "dark" : "light";
};

const applyResolvedTheme = (mode: ThemeMode): void => {
  const root = document.documentElement;
  root.setAttribute(THEME_ATTRIBUTE, mode);
  root.style.colorScheme = mode;
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
};

const applyThemePreference = (preference: ThemePreference): void => {
  applyResolvedTheme(resolveTheme(preference));
};

const syncThemeFromStorage = (): void => {
  applyThemePreference(readPreferenceFromStorage());
};

export const getThemePreference = (): ThemePreference => {
  return readPreferenceFromStorage();
};

export const setThemePreference = (preference: ThemePreference): void => {
  writePreferenceToStorage(preference);
  applyThemePreference(preference);
};

const subscribeToSystemThemeChanges = (): (() => void) => {
  const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY);
  const onChange = (): void => {
    if (readPreferenceFromStorage() === "system") {
      syncThemeFromStorage();
    }
  };

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }

  mediaQuery.addListener(onChange);
  return () => mediaQuery.removeListener(onChange);
};

declare global {
  interface Window {
    __tlxSetTheme?: (preference: ThemePreference) => void;
    __tlxGetTheme?: () => ThemePreference;
  }
}

export const initializeTheme = (): (() => void) => {
  syncThemeFromStorage();
  const cleanup = subscribeToSystemThemeChanges();

  // Dev helper until a dedicated settings UI is wired.
  window.__tlxSetTheme = setThemePreference;
  window.__tlxGetTheme = getThemePreference;

  return cleanup;
};
